import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import WizardSteps from "../WizardSteps/WizardSteps";
import FormField from "../FormField/FormField";
import ChoiceCard from "../ChoiceCard/ChoiceCard";
import WorklistTiming from "../WorklistTiming/WorklistTiming";
import ClinicField from "../ClinicField/ClinicField";
import AddSharedFolderModal from "../AddSharedFolderModal/AddSharedFolderModal";
import PacServerJobsForm, {
  AddressFields,
} from "../PacServerJobsForm/PacServerJobsForm";

import {
  DicomAddress,
  PacServerFormValues,
  buildDicomAddress,
  buildPacServerJobs,
  createPacServerFormValues,
  findDicomPacServerDevice,
  findPacServerJob,
  isSameAddress,
  pacServerFormValuesFromServer,
} from "src/utils/emr/pacServerHelper";
import { buildWorklistTiming } from "src/utils/emr/worklistTimingHelper";

import { useCreateEMRPacServer } from "src/services/emr/hooks/emr-pac-servers/useCreateEMRPacServer";
import { useUpdateEMRPacServer } from "src/services/emr/hooks/emr-pac-servers/useUpdateEMRPacServer";

import {
  EMRCatalogDeviceType,
  EMRHubType,
  EMRPacServerType,
} from "src/services/emr/types";

import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

type WorklistSource = "PacServer" | "SharedFolder";

const NEW_PAC_SERVER = "";

type AddWorklistModalType = {
  open: boolean;
  clinicId: string | undefined;
  hubId: string | undefined;
  hub: EMRHubType | undefined;
  devices: Array<EMRCatalogDeviceType>;
  pacServers: Array<EMRPacServerType> | undefined;
  listenPort: string;
  sharedFolderPaths: Array<string> | undefined;
  onClose: () => void;
};

const withWorklistOn = (values: PacServerFormValues): PacServerFormValues => ({
  ...values,
  worklist: { ...values.worklist, enabled: true },
  exams: values.exams.connectionId
    ? values.exams
    : { ...values.exams, enabled: true },
});

const AddWorklistModal = ({
  open,
  clinicId,
  hubId,
  hub,
  devices,
  pacServers,
  listenPort,
  sharedFolderPaths,
  onClose,
}: AddWorklistModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);

  const [step, setStep] = useState(0);
  const [source, setSource] = useState<WorklistSource>("PacServer");
  const [pacServerId, setPacServerId] = useState(NEW_PAC_SERVER);
  const [values, setValues] = useState<PacServerFormValues | null>(null);

  const dicomDevice = useMemo(
    () => findDicomPacServerDevice(devices),
    [devices]
  );

  const reusablePacServers = useMemo(
    () =>
      (pacServers ?? []).filter(
        (pacServer) =>
          pacServer.clinicId === clinicId &&
          !findPacServerJob(pacServer, ["Worklist"])?.enabled
      ),
    [pacServers, clinicId]
  );
  const existingPacServer = reusablePacServers.find(
    (pacServer) => pacServer.pacServerId === pacServerId
  );
  const device = existingPacServer
    ? devices.find((item) => item.key === existingPacServer.deviceKey)
    : dicomDevice;

  const { mutate: createPacServer, isPending: isCreating } =
    useCreateEMRPacServer(clinicId, hubId);
  const { mutate: updatePacServer, isPending: isUpdating } =
    useUpdateEMRPacServer(clinicId, hubId);

  const jobs = useMemo(
    () => (values ? buildPacServerJobs(values, device) : null),
    [values, device]
  );

  const handleClose = useCallback(() => {
    setStep(0);
    setSource("PacServer");
    setPacServerId(NEW_PAC_SERVER);
    setValues(null);
    onClose();
  }, [onClose]);

  const startPacServer = (id: string) => {
    const pacServer = reusablePacServers.find(
      (item) => item.pacServerId === id
    );
    setPacServerId(id);

    if (pacServer) {
      setValues(
        withWorklistOn(
          pacServerFormValuesFromServer(
            pacServer,
            devices.find((item) => item.key === pacServer.deviceKey),
            listenPort
          )
        )
      );
    } else if (dicomDevice) {
      setValues(
        withWorklistOn(createPacServerFormValues(dicomDevice, listenPort))
      );
    }
  };

  if (open && source === "SharedFolder" && step > 0) {
    return (
      <AddSharedFolderModal
        open
        clinicId={clinicId}
        hubId={hubId}
        sharedFolderPaths={sharedFolderPaths}
        fixedContent="WorklistCsv"
        title={t("Add a worklist")}
        leadingSteps={[t("Source")]}
        onBack={() => setStep(0)}
        onClose={handleClose}
      />
    );
  }

  const steps = [t("Source"), t("PAC server"), t("Query (optional)")];

  const worklistAddress: DicomAddress | undefined = values
    ? values.worklist.useServerAddress
      ? values.address
      : values.worklist.address
    : undefined;

  const setWorklistAddress = (address: DicomAddress) => {
    if (!values) return;

    if (existingPacServer) {
      setValues({
        ...values,
        worklist: {
          ...values.worklist,
          address,
          useServerAddress: isSameAddress(address, values.address),
        },
      });
    } else {
      setValues({
        ...values,
        address,
        worklist: { ...values.worklist, address },
        exams: values.exams.useServerAddress
          ? { ...values.exams, address }
          : values.exams,
      });
    }
  };

  const isStepValid = [
    Boolean(clinicId) && (source === "SharedFolder" || Boolean(dicomDevice)),
    Boolean(values) &&
      Boolean(worklistAddress && buildDicomAddress(worklistAddress)) &&
      Boolean(values && buildWorklistTiming(values.worklist.timing)) &&
      (Boolean(existingPacServer) || Boolean(values?.name.trim())),
    jobs !== null,
  ][step];

  const handleNext = () => {
    if (step === 0 && source === "PacServer" && !values) {
      startPacServer(NEW_PAC_SERVER);
    }
    setStep(step + 1);
  };

  const handleCreate = () => {
    if (!clinicId || !values || !jobs || !device) return;

    if (existingPacServer) {
      updatePacServer(
        {
          pacServerId: existingPacServer.pacServerId,
          name: existingPacServer.name,
          deviceKey: existingPacServer.deviceKey,
          jobs,
        },
        { onSuccess: handleClose }
      );
      return;
    }

    createPacServer(
      {
        clinicId,
        name: values.name.trim(),
        deviceKey: device.key,
        jobs,
      },
      { onSuccess: handleClose }
    );
  };

  const pacServerOptions = [
    { displayName: t("New DICOM PAC server"), value: NEW_PAC_SERVER },
    ...reusablePacServers.map((pacServer) => ({
      displayName: pacServer.name,
      value: pacServer.pacServerId,
    })),
  ];

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Add a worklist")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            width: "100%",
          }}
        >
          <Button
            sx={ModalCustomCancel}
            onClick={step === 0 ? handleClose : () => setStep(step - 1)}
          >
            {step === 0 ? t("Cancel") : t("Back")}
          </Button>
          {step < steps.length - 1 ? (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isStepValid}
              onClick={handleNext}
            >
              {t("Next")}
            </Button>
          ) : (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isStepValid || isCreating || isUpdating}
              onClick={handleCreate}
            >
              {t("Create worklist")}
            </Button>
          )}
        </Grid>
      }
    >
      <WizardSteps
        steps={source === "SharedFolder" ? [t("Source"), t("Folder")] : steps}
        current={step}
      />
      <Box display="flex" flexDirection="column" gap="16px" p="16px">
        {step === 0 && (
          <>
            <Box display="flex" flexDirection="column" gap="8px">
              <Typography fontSize="15px" fontWeight={800}>
                {t("Where does the clinic's schedule come from?")}
              </Typography>
              <ChoiceCard
                name="add-worklist-source"
                value="PacServer"
                checked={source === "PacServer"}
                title={t("PAC server")}
                description={t(
                  "The hub asks a DICOM PAC server or RIS for today's appointments, and can then query it for those patients' exams."
                )}
                onSelect={() => setSource("PacServer")}
              />
              <ChoiceCard
                name="add-worklist-source"
                value="SharedFolder"
                checked={source === "SharedFolder"}
                title={t("Shared folder")}
                description={t(
                  "Your EMR exports a CSV to a folder and the hub reads it at set times."
                )}
                onSelect={() => setSource("SharedFolder")}
              />
            </Box>
            <ClinicField
              idPrefix="add-worklist"
              hub={hub}
              clinicId={clinicId}
            />
          </>
        )}

        {step === 1 && values && worklistAddress && (
          <>
            {reusablePacServers.length > 0 && (
              <FormField
                label={t("PAC server")}
                labelId="add-worklist-pac-server-label"
                required
              >
                <SelectDropdown
                  id="add-worklist-pac-server-dropdown"
                  labelId="add-worklist-pac-server-label"
                  dropdownOptions={pacServerOptions}
                  value={
                    pacServerOptions.find(
                      (option) => option.value === pacServerId
                    )?.displayName ?? ""
                  }
                  handleChange={(event) => {
                    const id = pacServerOptions.find(
                      (option) => option.displayName === event.target.value
                    )?.value;
                    if (id !== undefined) startPacServer(id);
                  }}
                />
              </FormField>
            )}
            {!existingPacServer && (
              <FormField
                label={t("Name")}
                htmlFor="add-worklist-name-input"
                required
              >
                <TextInputField
                  id="add-worklist-name-input"
                  placeholder={t("e.g. Main PAC server")}
                  value={values.name}
                  maxLength={100}
                  onChange={(event) =>
                    setValues({ ...values, name: event.target.value })
                  }
                />
              </FormField>
            )}
            <AddressFields
              idPrefix="add-worklist"
              value={worklistAddress}
              disabled={false}
              remoteAeLabel={t("PAC server AE title")}
              showHost
              onChange={setWorklistAddress}
            />
            <Typography fontSize="15px" fontWeight={800}>
              {t("How often")}
            </Typography>
            <WorklistTiming
              idPrefix="add-worklist"
              value={values.worklist.timing}
              onChange={(timing) =>
                setValues({
                  ...values,
                  worklist: { ...values.worklist, timing },
                })
              }
            />
            <Box maxWidth="260px">
              <FormField
                label={t("Scheduled station AE title")}
                htmlFor="add-worklist-station-ae-input"
              >
                <TextInputField
                  id="add-worklist-station-ae-input"
                  placeholder={t("Optional")}
                  value={values.worklist.scheduledStationAeTitle}
                  maxLength={16}
                  onChange={(event) =>
                    setValues({
                      ...values,
                      worklist: {
                        ...values.worklist,
                        scheduledStationAeTitle: event.target.value,
                      },
                    })
                  }
                />
              </FormField>
            </Box>
          </>
        )}

        {step === 2 && values && (
          <>
            <Box>
              <Typography fontSize="15px" fontWeight={800}>
                {t("Query the PAC server for these patients' exams?")}
              </Typography>
              <Typography fontSize="12px" color={mutedText}>
                {t(
                  "Optional. The address is the worklist's; change it under More options if exams live elsewhere."
                )}
              </Typography>
            </Box>
            <PacServerJobsForm
              idPrefix="add-worklist"
              values={values}
              device={device}
              hubHasOtherWorklist
              onChange={setValues}
              jobs={["exams"]}
            />
          </>
        )}
      </Box>
    </DefaultModal>
  );
};

export default AddWorklistModal;
